import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, CheckCircle, Gift, Users } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { useAuth } from "@/shared/contexts/AuthContext";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { useToast } from "@/shared/hooks/use-toast";

interface ReferralData {
  referral_code: string;
  referral_link: string;
  referral_id?: string;
  rocket_referral_id?: string;
}

export default function ReferFriendPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!user?.email) {
      setError('You must be logged in to access the referral program');
      setLoading(false);
      return;
    }

    fetchReferralData();
  }, [user]);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      console.log('[ReferFriendPage] Fetching referral data for:', user?.email);

      // Call backend API to get/create referral participant
      const response = await apiClient.post<{
        success: boolean;
        data: ReferralData;
      }>(API_ROUTES.USER_CREATE_REFERRAL, {
        campaign_id: 'L6usufOo', // B2B campaign
        campaign_type: 'b2b',
        referrer_email: user?.email,
        first_name: user?.user_metadata?.first_name || user?.user_metadata?.given_name,
        last_name: user?.user_metadata?.last_name || user?.user_metadata?.family_name,
      });

      if (response.success && response.data) {
        console.log('[ReferFriendPage] ✅ Got referral data:', response.data);
        setReferralData(response.data);
      } else {
        throw new Error('Failed to fetch referral data');
      }
    } catch (err: any) {
      console.error('[ReferFriendPage] ❌ Error fetching referral data:', err);
      setError(err.message || 'Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    if (!referralData?.referral_link) return;

    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      toast({
        title: "Link Copied!",
        description: "Your referral link has been copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Could not copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Refer a Friend</h1>
          <p className="text-gray-600 mt-2">
            Share Wlkthru with your friends and get $100 off when they subscribe!
          </p>
        </div>

        {/* Error State */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your referral link...</p>
            </CardContent>
          </Card>
        )}

        {/* Referral Content */}
        {!loading && !error && referralData && (
          <div className="space-y-6">
            {/* Referral Link Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-primary" />
                  Your Referral Link
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={referralData.referral_link}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    onClick={copyReferralLink}
                    variant="outline"
                    className="shrink-0"
                  >
                    {linkCopied ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <Button
                  onClick={copyReferralLink}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  {linkCopied ? (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Link Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy Referral Link
                    </>
                  )}
                </Button>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900 font-semibold">
                    How it works:
                  </p>
                  <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                    <li>Share your unique referral link with friends</li>
                    <li>They get $100 off their subscription (save $33.33/month for 3 months)</li>
                    <li>You get $100 off when they subscribe (save $33.33/month for 3 months)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Referral Code Card */}
            <Card>
              <CardHeader>
                <CardTitle>Your Referral Code</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center p-6 bg-gradient-to-r from-primary/10 to-blue-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-2">Share this code:</div>
                    <div className="text-3xl font-bold text-primary tracking-wider">
                      {referralData.referral_code}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
